import { PrinterOutlined } from '@ant-design/icons';
import type { AllocateResult, ApplyItemRow, ApproveAction } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import { formatAmount, formatDisplayTime, formatQty } from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Card, Descriptions, Form, Input, Modal, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { hq } from '../api/client';
import ApprovalTracePanel from '../components/ApprovalTracePanel';
import { SlaBadge } from '../components/SlaBadge';
import { ApplyStatusTag } from '../components/StatusTag';
import { printDocument } from '../print';
import { useAuthStore } from '../stores/auth';
import { ellipsisCell, StickyActionBar, TipText, sumWidth } from '@hq/ui-kit';

/** 判源结论（docs/03 §2.9）。 */
const ALLOCATE_META: Record<string, { color: string; text: string }> = {
  INNER: { color: 'green', text: '院内供应' },
  OUTER: { color: 'blue', text: '外仓代履' },
  SPLIT: { color: 'orange', text: '院内+外仓拆分' },
  PURCHASE: { color: 'red', text: '需采购' }
};

const SOURCE_MODE_TEXT: Record<string, string> = {
  AUTO: '自动判源',
  INNER_ONLY: '仅院内',
  OUTER_FORCE: '强制外仓'
};

/**
 * 指定审批人（仅未配审批流时用）。手填 user_id 等于让临床去猜主键，
 * 改成按姓名/工号搜索（`/wl/approval/user-search`），选中顺便回填姓名。
 */
function ApproverSelect({
  value,
  onChange,
  onPick
}: {
  value?: string;
  onChange?: (value?: string) => void;
  onPick?: (name?: string) => void;
}) {
  const [keyword, setKeyword] = useState('');
  const users = useQuery({
    queryKey: ['approval', 'user-search', keyword],
    staleTime: 30_000,
    queryFn: () => hq.approval.searchUsers(keyword || undefined, 20)
  });
  const rows = users.data ?? [];
  return (
    <Select
      allowClear
      showSearch
      value={value}
      placeholder="搜索姓名 / 工号"
      filterOption={false}
      onSearch={setKeyword}
      onChange={(next) => {
        onChange?.(next);
        onPick?.(rows.find((row) => row.id === next)?.name ?? undefined);
      }}
      loading={users.isFetching}
      options={rows.map((row) => ({ value: row.id, label: `${row.name ?? '未命名'}（${row.userCode ?? row.id}）` }))}
    />
  );
}

/** 申领详情：提交审批 + 同意/驳回（端点为 Hq.Api 已实现的 4 个）。 */
export default function ApplyDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);

  const [submitOpen, setSubmitOpen] = useState(false);
  const [auditOpen, setAuditOpen] = useState<ApproveAction | null>(null);
  const [allocateResult, setAllocateResult] = useState<AllocateResult | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [submitForm] = Form.useForm<{ approverUserId: string; approverName?: string }>();
  const [auditForm] = Form.useForm<{ opinion?: string }>();
  const [cancelForm] = Form.useForm<{ reason?: string }>();

  /**
   * 按钮级权限守卫：后端在判源/生成配送单上做了 EnsurePerm，
   * 这里保持同一口径，避免出现「按钮可见、点击必 403」。
   */
  const can = (perm: string): boolean =>
    profile?.level === 'admin' || hasPermission(profile?.perms, perm);
  const canAllocate = can('wl:apply:approve') || can('wl:purchase:manage');
  const canCreateDelivery = can('wl:purchase:manage');

  const detail = useQuery({
    queryKey: ['apply', id],
    placeholderData: keepPreviousData,
    queryFn: () => hq.apply.detail(id),
    enabled: id !== ''
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['apply'] });
  };

  const submitMutation = useMutation({
    mutationFn: (values: { approverUserId?: string; approverName?: string }) => hq.apply.submit(id, values),
    onSuccess: async (data) => {
      message.success(
        data.flowId !== '0' && data.approvers.length > 0
          ? `已提交：${data.nodeName ?? '审批'}（${data.approvers.join('、')}）`
          : '已提交审批'
      );
      setSubmitOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '提交失败')
  });

  const auditMutation = useMutation({
    mutationFn: (values: { action: ApproveAction; opinion?: string }) => hq.apply.approve(id, values),
    onSuccess: async (data, values) => {
      const next =
        data.status === 'RUNNING' || data.status === 'WAITING'
          ? `，流转至「${data.nodeName ?? data.nodeCode ?? '下一节点'}」`
          : '';
      message.success(values.action === 'APPROVE' ? `已同意${next}` : '已驳回');
      setAuditOpen(null);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '操作失败')
  });

  // 货源判源：院内可用库存 → 外部零库存仓 → 采购缺口；外仓部分生成 GY 供货指令
  const allocateMutation = useMutation({
    mutationFn: () => hq.apply.allocate(id),
    onSuccess: async (data) => {
      setAllocateResult(data);
      message.success(
        data.outerLines > 0
          ? `判源完成：外仓承接 ${data.outerLines} 行，已生成供货指令 ${data.supplyBizNo ?? ''}`
          : '判源完成：全部由院内库存满足'
      );
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '判源失败')
  });

  /**
   * 一键履约：审批通过后一步完成「判源 → 外仓供货指令 / 转请购 → 生成配送单」。
   * 后端 `POST /api/wl/apply/{id}/fulfill` 幂等（重放返回 created=false），
   * 用于审批自动触发失败后的人工重试。
   */
  const fulfillMutation = useMutation({
    mutationFn: () => hq.apply.fulfill(id),
    onSuccess: async (data) => {
      const parts: string[] = [];
      if (data.supplyBizNo) parts.push(`供货指令 ${data.supplyBizNo}`);
      if (data.requireBizNo) parts.push(`请购单 ${data.requireBizNo}`);
      if (data.deliveries.length > 0) parts.push(`配送单 ${data.deliveries.map((row) => row.bizNo).join('、')}`);
      message.success(
        data.created
          ? `履约推进完成：${parts.length > 0 ? parts.join('，') : '全部由院内库存满足'}`
          : '履约已推进过，未重复生成下游单据'
      );
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '履约推进失败')
  });

  // 审批通过后生成配送单（直送）：后端幂等，重复点击返回已有配送单
  const deliveryMutation = useMutation({
    mutationFn: () =>
      hq.delivery.createFromApply({
        applyId: id,
        deliverUserId: profile?.userId ?? '',
        deliverName: profile?.name
      }),
    onSuccess: (data) => {
      message.success('配送单已生成');
      navigate(`/delivery/${data.delivery.id}`);
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '生成配送单失败')
  });

  // 取消申领：释放判源预占库存 + 预算冻结（后端实现），替代「等预占超期自动释放」
  const cancelMutation = useMutation({
    mutationFn: (reason?: string) => hq.apply.cancel(id, reason),
    onSuccess: async (data) => {
      message.success(
        data.releasedQty > 0 ? `已取消，释放预占 ${formatQty(data.releasedQty)}` : '已取消'
      );
      setCancelOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '取消失败')
  });

  const apply = detail.data?.apply;
  const items = detail.data?.items ?? [];

  // 后端 CancelAsync 仅拒绝 SIGNED/PART_SIGNED/CLOSED/SETTLED/CANCELED，其余状态可主动撤销
  const cancelableStatuses = ['DRAFT', 'SUBMITTED', 'APPROVING', 'APPROVED', 'ALLOCATED', 'DELIVERING'];
  const canCancel = cancelableStatuses.includes(apply?.bizStatus ?? '');

  const columns: ColumnsType<ApplyItemRow> = [
    { title: '行号', dataIndex: 'lineNo', width: 70 },
    { title: '物资 ID', dataIndex: 'materialId', width: 180 },
    { title: '品名', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: (value: string | null) => (value ? <TipText text={value} /> : '-') },
    { title: '规格', dataIndex: 'spec', ellipsis: { showTitle: false }, render: (value: string | null) => (value ? <TipText text={value} /> : '-') },
    { title: '单位', dataIndex: 'unitName', width: 90, render: (value: string | null) => value || '-' },
    { title: '数量', dataIndex: 'reqQty', width: 110, align: 'right', render: (value: number) => formatQty(value) },
    { title: '单价', dataIndex: 'price', width: 120, align: 'right', render: (value: number) => formatAmount(value, 4) },
    { title: '金额', dataIndex: 'amount', width: 130, align: 'right', render: (value: number) => formatAmount(value) },
    {
      title: '判源结果',
      dataIndex: 'allocateResult',
      width: 150,
      render: (value: string | null | undefined, row) => {
        if (!value) return <Typography.Text type="secondary">待判源</Typography.Text>;
        const meta = ALLOCATE_META[value] ?? { color: 'default', text: value };
        const extra: string[] = [];
        if ((row.outerAllocateQty ?? 0) > 0) extra.push(`外仓 ${formatQty(row.outerAllocateQty ?? 0)}`);
        if ((row.stockShortQty ?? 0) > 0) extra.push(`缺口 ${formatQty(row.stockShortQty ?? 0)}`);
        return (
          // 单行展示：外仓/缺口数量收进悬浮提示，避免行高被拉高
          <Tooltip title={extra.length > 0 ? extra.join(' / ') : undefined} placement="topLeft">
            <Tag color={meta.color} style={{ marginInlineEnd: 0 }}>
              {meta.text}
            </Tag>
          </Tooltip>
        );
      }
    }
  ];

  if (detail.isError) {
    return <Alert type="error" showIcon message="申领单加载失败" description="可能已被删除或不在你的数据范围内" />;
  }

  return (
    <>
      <Card
        className="page-card"
        title="申领单详情"
        loading={id !== '' && detail.isPending}
        extra={
          <Space>
            <Button
              icon={<PrinterOutlined />}
              disabled={!apply}
              onClick={() =>
                apply &&
                printDocument({
                  title: '申领单',
                  subtitle: apply.bizNo,
                  meta: [
                    ['申请科室', apply.applyDeptName || '-'],
                    ['紧急程度', apply.urgentLevel || '-'],
                    ['状态', apply.bizStatus],
                    ['申请时间', formatDisplayTime(apply.bizDate)],
                    ['应到时间', apply.slaDeadline ? formatDisplayTime(apply.slaDeadline) : '-'],
                    ['数量合计', formatQty(apply.totalQty)],
                    ['金额合计', formatAmount(apply.totalAmount)]
                  ],
                  columns: [
                    { title: '行' },
                    { title: '物资编码' },
                    { title: '物资名称' },
                    { title: '规格' },
                    { title: '单位' },
                    { title: '数量', align: 'right' },
                    { title: '单价', align: 'right' },
                    { title: '金额', align: 'right' }
                  ],
                  rows: items.map((item) => [
                    item.lineNo,
                    item.materialCode ?? '',
                    item.materialName ?? '',
                    item.spec ?? '',
                    item.unitName ?? '',
                    formatQty(item.reqQty),
                    formatAmount(item.price, 4),
                    formatAmount(item.amount)
                  ]),
                  footer: `收货位置：${apply.spaceDesc || '-'}　备注：${apply.applyTitle || '-'}`
                })
              }
            >
              打印
            </Button>
            <Button onClick={() => navigate('/apply')}>返回列表</Button>
          </Space>
        }
      >
        {apply ? (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="单号">{apply.bizNo}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <ApplyStatusTag status={apply.bizStatus} />
            </Descriptions.Item>
            <Descriptions.Item label="标题">{apply.applyTitle || '-'}</Descriptions.Item>
            <Descriptions.Item label="申请科室">{apply.applyDeptName || '-'}</Descriptions.Item>
            <Descriptions.Item label="紧急程度">{apply.urgentLevel || '-'}</Descriptions.Item>
            <Descriptions.Item label="时效">
              <SlaBadge
                deadline={apply.slaDeadline}
                finished={['SIGNED', 'CLOSED', 'CANCELED', 'REJECTED'].includes(apply.bizStatus)}
                finishedText={apply.bizStatus === 'SIGNED' ? '已签收' : undefined}
              />
            </Descriptions.Item>
            <Descriptions.Item label="收货位置" span={2}>{apply.spaceDesc || '-'}</Descriptions.Item>
            <Descriptions.Item label="数量合计">{formatQty(apply.totalQty)}</Descriptions.Item>
            <Descriptions.Item label="金额合计">{formatAmount(apply.totalAmount)}</Descriptions.Item>
            <Descriptions.Item label="申请时间">{formatDisplayTime(apply.bizDate)}</Descriptions.Item>
            <Descriptions.Item label="提交时间">{formatDisplayTime(apply.submitAt)}</Descriptions.Item>
            <Descriptions.Item label="审批时间">{formatDisplayTime(apply.approveAt)}</Descriptions.Item>
            <Descriptions.Item label="审批实例">{apply.instanceId}</Descriptions.Item>
          </Descriptions>
        ) : null}
      </Card>

      <Card className="page-card" title="明细">
        <Table<ApplyItemRow> rowKey="id" size="small" columns={columns} dataSource={items} pagination={false} scroll={{ x: sumWidth(columns) }} />
      </Card>

      {apply ? <ApprovalTracePanel instanceId={apply.instanceId} onChanged={invalidate} /> : null}

      {apply ? (
        <StickyActionBar>
          {apply.bizStatus === 'DRAFT' ? (
            <Button type="primary" onClick={() => setSubmitOpen(true)}>
              提交审批
            </Button>
          ) : null}
          {apply.bizStatus === 'APPROVED' && canAllocate ? (
            <Button
              type="primary"
              loading={allocateMutation.isPending}
              onClick={() =>
                modal.confirm({
                  title: '货源判源',
                  content:
                    '按「院内可用库存 → 外部零库存仓 → 采购缺口」逐行判源；外仓承接部分会生成一张外仓供货指令（GY 单），判源后申领单不可再改。',
                  okText: '开始判源',
                  onOk: () => allocateMutation.mutateAsync()
                })
              }
            >
              货源判源
            </Button>
          ) : null}
          {apply.bizStatus === 'APPROVED' && canAllocate ? (
            <Tooltip title="一步完成：判源（预占）→ 外仓供货指令/转请购 → 生成配送单。审批自动履约失败时点此重试。">
              <Button
                loading={fulfillMutation.isPending}
                onClick={() =>
                  modal.confirm({
                    title: '一键履约推进？',
                    content: '将执行判源并按结果生成供货指令/请购单与配送单；已推进过不会重复生成。',
                    okText: '开始推进',
                    cancelText: '取消',
                    onOk: () => fulfillMutation.mutateAsync()
                  })
                }
              >
                一键履约
              </Button>
            </Tooltip>
          ) : null}
          {(apply.bizStatus === 'APPROVED' || apply.bizStatus === 'ALLOCATED') && canCreateDelivery ? (
            <Button loading={deliveryMutation.isPending} onClick={() => deliveryMutation.mutate()}>
              生成配送单
            </Button>
          ) : null}
          {/* 后端 ApproveAsync 仅允许 SUBMITTED / APPROVING */}
          {apply.bizStatus === 'SUBMITTED' || apply.bizStatus === 'APPROVING' ? (
            <>
              <Button type="primary" onClick={() => setAuditOpen('APPROVE')}>
                同意
              </Button>
              <Button danger onClick={() => setAuditOpen('REJECT')}>
                驳回
              </Button>
            </>
          ) : null}
          {canCancel ? (
            <Button danger onClick={() => setCancelOpen(true)}>
              取消申领
            </Button>
          ) : null}
        </StickyActionBar>
      ) : null}

      <Modal
        title="提交审批"
        open={submitOpen}
        onCancel={() => setSubmitOpen(false)}
        destroyOnHidden
        onOk={async () => {
          const values = await submitForm.validateFields();
          submitMutation.mutate(values);
        }}
        confirmLoading={submitMutation.isPending}
      >
        <Form form={submitForm} layout="vertical" preserve={false}>
          <Typography.Paragraph type="secondary">
            已配置审批流时，审批人由引擎按 cfg_approval_flow 自动解析（科室负责人 → 院级 → 财务），提交无需填写。
          </Typography.Paragraph>
          <Form.Item name="approverUserId" label="指定审批人（仅未配流时使用）">
            <ApproverSelect onPick={(name) => submitForm.setFieldValue('approverName', name ?? '')} />
          </Form.Item>
          <Form.Item name="approverName" label="审批人姓名">
            <Input placeholder="选定审批人后自动回填" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={auditOpen === 'APPROVE' ? '同意' : '驳回'}
        open={auditOpen !== null}
        onCancel={() => setAuditOpen(null)}
        destroyOnHidden
        onOk={async () => {
          const values = await auditForm.validateFields();
          if (!auditOpen) return;
          auditMutation.mutate({ action: auditOpen, opinion: values.opinion });
        }}
        confirmLoading={auditMutation.isPending}
      >
        <Form form={auditForm} layout="vertical" preserve={false}>
          <Form.Item name="opinion" label="意见" rules={auditOpen === 'REJECT' ? [{ required: true, message: '驳回必须填写意见' }] : []}>
            <Input.TextArea rows={3} maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="判源结果"
        open={allocateResult !== null}
        onCancel={() => setAllocateResult(null)}
        footer={<Button onClick={() => setAllocateResult(null)}>知道了</Button>}
        width={960}
      >
        {allocateResult ? (
          <>
            <Descriptions size="small" column={2} bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="申领单">{allocateResult.bizNo}</Descriptions.Item>
              <Descriptions.Item label="判源后状态">{allocateResult.status}</Descriptions.Item>
              <Descriptions.Item label="外仓供货指令">
                {allocateResult.supplyOrderId === '0' ? '未涉及（全部院内满足）' : allocateResult.supplyBizNo ?? '-'}
              </Descriptions.Item>
              <Descriptions.Item label="外部零库存仓">
                {allocateResult.outerWarehouseId === '0'
                  ? '未命中外仓'
                  : allocateResult.outerWarehouseName ?? allocateResult.outerWarehouseId}
              </Descriptions.Item>
              <Descriptions.Item label="行分布" span={2}>
                <Space size={8}>
                  <Tag color="green">院内 {allocateResult.innerLines}</Tag>
                  <Tag color="blue">外仓 {allocateResult.outerLines}</Tag>
                  <Tag color="orange">拆分 {allocateResult.splitLines}</Tag>
                  <Tag color="red">采购 {allocateResult.purchaseLines}</Tag>
                </Space>
              </Descriptions.Item>
            </Descriptions>

            <Table
              rowKey="applyItemId"
              size="small"
              pagination={false}
              dataSource={allocateResult.lines}
              scroll={{ x: 900 }}
              columns={[
                { title: '行', dataIndex: 'lineNo', width: 60 },
                { title: '物资', dataIndex: 'materialName', ellipsis: { showTitle: false }, render: ellipsisCell('materialName') },
                { title: '需求', dataIndex: 'needQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
                { title: '院内', dataIndex: 'innerQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
                { title: '外仓', dataIndex: 'outerQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
                { title: '缺口', dataIndex: 'shortQty', width: 100, align: 'right', render: (value: number) => formatQty(value) },
                {
                  title: '结论',
                  dataIndex: 'allocateResult',
                  width: 140,
                  render: (value: string) => {
                    const meta = ALLOCATE_META[value] ?? { color: 'default', text: value };
                    return <Tag color={meta.color}>{meta.text}</Tag>;
                  }
                },
                {
                  title: '货源模式',
                  dataIndex: 'sourceMode',
                  width: 120,
                  render: (value: string) => SOURCE_MODE_TEXT[value] ?? value
                }
              ]}
            />
          </>
        ) : null}
      </Modal>

      <Modal
        title="取消申领"
        open={cancelOpen}
        onCancel={() => setCancelOpen(false)}
        destroyOnHidden
        okText="确认取消"
        okButtonProps={{ danger: true }}
        confirmLoading={cancelMutation.isPending}
        onOk={async () => {
          const values = await cancelForm.validateFields();
          cancelMutation.mutate(values.reason);
        }}
      >
        <Typography.Paragraph type="secondary">
          取消后单据不可恢复，判源时预占的库存与已冻结的预算将被释放。
        </Typography.Paragraph>
        <Form form={cancelForm} layout="vertical" preserve={false}>
          <Form.Item name="reason" label="取消原因">
            <Input.TextArea rows={3} maxLength={200} showCount />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
